import { Fragment, useContext } from 'react';
import Compare from '../Compare';
import Stack from '../../../shared/layouts/Stack';
import CategoryHeader from '../CategoryHeader';
import {CompareContext} from '../ComparePage';
import CompareOrvCoverageSection from './CompareOrvCoverageSection';
import {getPeriodOptions} from '../../../../utils/constants';
import {useTranslation} from "react-i18next";

const SECTION = 'orv_general';

const CompareOrvSection = () => {
  const {
    showOnlyDiff,
    hasDiff,
    shouldShow,
    ownInsuranceData,
    handleInsuranceDataChange,
    otherInsuranceData,
    meta: { insuranceProviders = [] },
    orvFields,
    orvCoverageFields,
    hasSectionCopiedFields,
  } = useContext(CompareContext);

  const { t } = useTranslation();

  const handleOrvGeneralDataChange =
    (index, section, fieldName) => (e) => {
      handleInsuranceDataChange(e, index, section, fieldName);
    };

  const renderInsurances =
    ownInsuranceData?.length > otherInsuranceData?.length
      ? ownInsuranceData
      : otherInsuranceData;

  return renderInsurances?.map((insurance, index) => {
    const ownInsurance = ownInsuranceData?.[index] || {};
    const otherInsurance = otherInsuranceData?.[index] || {};
    const SECTION_INSURANCE = `${SECTION}_${index}`;

    if (
      showOnlyDiff &&
      !hasSectionCopiedFields(SECTION_INSURANCE) &&
      !hasDiff(orvFields, ownInsurance, otherInsurance) &&
      !ownInsurance?.coverages?.some((cov, index) =>
        hasDiff(orvCoverageFields, cov, otherInsurance?.coverages?.[index])
      )
    )
      return null;

    return (
      <Fragment key={`insurance_${index}`}>
        <Stack space="l">
          <CategoryHeader>
            <span>{t('Overlijdensrisicoverzekering {{index}}', {index: index + 1})}</span>
            <span>{t('Overlijdensrisicoverzekering {{index}}', {index: index + 1})}</span>
          </CategoryHeader>
          <Stack space="m">
            {shouldShow(
              'insurance_company',
              ownInsurance,
              otherInsurance,
              SECTION_INSURANCE
            ) && (
              <Compare
                label={t("Maatschappij")}
                name="insurance_company"
                section={SECTION_INSURANCE}
                leftValue={ownInsurance?.insurance_company}
                rightValue={otherInsurance?.insurance_company}
                valueTransformer={(value) => {
                  const provider = insuranceProviders.find(
                    (provider) => provider.value === value
                  );
                  return provider ? provider.label : value;
                }}
                onUpdate={handleOrvGeneralDataChange(
                  index,
                  SECTION_INSURANCE,
                  'insurance_company'
                )}
              />
            )}
            {shouldShow(
              'policy_number',
              ownInsurance,
              otherInsurance,
              SECTION_INSURANCE
            ) && (
              <Compare
                label={t("Polisnummer")}
                name="policy_number"
                section={SECTION_INSURANCE}
                leftValue={ownInsurance?.policy_number}
                rightValue={otherInsurance?.policy_number}
                onUpdate={handleOrvGeneralDataChange(
                  index,
                  SECTION_INSURANCE,
                  'policy_number'
                )}
              />
            )}
            {shouldShow(
              'premium.amount',
              ownInsurance,
              otherInsurance,
              SECTION_INSURANCE
            ) && (
              <Compare
                label={t("Premie")}
                name="premium.amount"
                section={SECTION_INSURANCE}
                leftValue={ownInsurance?.premium?.amount}
                rightValue={otherInsurance?.premium?.amount}
                onUpdate={handleOrvGeneralDataChange(
                  index,
                  SECTION_INSURANCE,
                  'premium.amount'
                )}
                isCurrency
              />
            )}
            {shouldShow(
              'premium.period',
              ownInsurance,
              otherInsurance,
              SECTION_INSURANCE
            ) && (
              <Compare
                label={t("Per")}
                name="premium.period"
                section={SECTION_INSURANCE}
                leftValue={ownInsurance?.premium?.period}
                rightValue={otherInsurance?.premium?.period}
                valueTransformer={(value) => {
                  const period = getPeriodOptions().find(
                    (period) => period.value === value
                  );
                  return period ? period.label : value;
                }}
                onUpdate={handleOrvGeneralDataChange(
                  index,
                  SECTION_INSURANCE,
                  'premium.period'
                )}
              />
            )}
            {shouldShow(
              'premium.duration_in_months',
              ownInsurance,
              otherInsurance,
              SECTION_INSURANCE
            ) && (
              <Compare
                label={t("Looptijd")}
                name="premium.duration_in_months"
                section={SECTION_INSURANCE}
                leftValue={ownInsurance?.premium?.duration_in_months}
                rightValue={otherInsurance?.premium?.duration_in_months}
                valueTransformer={(value) => value && !isNaN(value) ? `${value} maanden` : null}
                onUpdate={handleOrvGeneralDataChange(
                  index,
                  SECTION_INSURANCE,
                  'premium.duration_in_months'
                )}
              />
            )}
            {shouldShow(
              'pawned',
              ownInsurance,
              otherInsurance,
              SECTION_INSURANCE
            ) && (
              <Compare
                label={t("Verpand")}
                name="pawned"
                section={SECTION_INSURANCE}
                leftValue={ownInsurance?.pawned}
                rightValue={otherInsurance?.pawned}
                onUpdate={handleOrvGeneralDataChange(
                  index,
                  SECTION_INSURANCE,
                  'pawned'
                )}
              />
            )}
          </Stack>
        </Stack>
        <CompareOrvCoverageSection insuranceIndex={index} />
      </Fragment>
    );
  }) || null;
};

export default CompareOrvSection;
